import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Gift, MapPin, Mail, User, Clock } from 'lucide-react';

const API = '/api';

// Referrals are a SEPARATE lane from ad leads: read + manual assign only. NOT wired into the
// ad-lead reminder engine or the lead-status button system.
const STATUS = {
  new:       { label: 'New',       color: 'text-hg-green border-hg-green/40 bg-hg-green/10' },
  assigned:  { label: 'Assigned',  color: 'text-blue-300 border-blue-400/40 bg-blue-400/10' },
  contacted: { label: 'Contacted', color: 'text-purple-300 border-purple-400/40 bg-purple-400/10' },
  won:       { label: 'Won',       color: 'text-hg-green border-hg-green/50 bg-hg-green/15' },
  lost:      { label: 'Lost',      color: 'text-hg-text-dim border-hg-border bg-hg-surface/40' },
};
const STATUS_ORDER = ['new', 'assigned', 'contacted', 'won', 'lost'];

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
}

export default function Referrals() {
  const [referrals, setReferrals] = useState([]);
  const [counts, setCounts] = useState({});
  const [fitters, setFitters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [busyId, setBusyId] = useState(null);
  const [showTest, setShowTest] = useState(false);   // OFF (default) hides is_test referrals

  const reload = useCallback(async () => {
    setError('');
    try {
      const res = await axios.get(`${API}/referrals${showTest ? '?include_test=true' : ''}`);
      setReferrals(res.data.referrals || []);
      setCounts(res.data.counts || {});
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load referrals.');
    }
  }, [showTest]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await reload();
      try {
        const f = await axios.get(`${API}/promoted-fitters/list?only_active=true`);
        setFitters(f.data.fitters || []);
      } catch { /* fitter list is optional for viewing */ }
      setLoading(false);
    })();
  }, [reload]);

  const assign = async (ref, fitterId) => {
    if (!fitterId) return;
    const fitter = fitters.find((f) => f.id === fitterId);
    setBusyId(ref.id);
    try {
      await axios.post(`${API}/referrals/${ref.id}/assign`, {
        fitter_id: fitterId, fitter_name: fitter?.name || '',
      });
      await reload();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not assign.');
    } finally { setBusyId(null); }
  };

  const setStatus = async (ref, status) => {
    setBusyId(ref.id);
    try {
      await axios.post(`${API}/referrals/${ref.id}/status`, { status });
      await reload();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not update status.');
    } finally { setBusyId(null); }
  };

  const shown = filter === 'all' ? referrals : referrals.filter((r) => r.status === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="referrals-loading">
        <Loader2 className="animate-spin text-hg-green" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="referrals-page">
      <div>
        <h1 className="font-display text-2xl lg:text-3xl text-hg-text tracking-wider">Referrals</h1>
        <p className="text-sm text-hg-text-dim font-sans mt-1">
          Golfers your customers referred through the feedback survey. Assign a fitter and move each along —
          this is a separate lane from ad leads.
        </p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-400/30 text-red-300 rounded-sm px-4 py-2 text-sm font-sans">
          {error}
        </div>
      )}

      {/* filter chips + counts */}
      <div className="flex flex-wrap gap-2 items-center">
        <button
          onClick={() => setFilter('all')}
          data-testid="referrals-filter-all"
          className={`text-xs font-sans uppercase tracking-wider rounded-sm px-2.5 py-1 border ${
            filter === 'all' ? 'border-hg-green text-hg-green bg-hg-green/10' : 'border-hg-border text-hg-text-dim hover:border-hg-green'
          }`}
        >
          All {counts.total ? `(${counts.total})` : ''}
        </button>
        {STATUS_ORDER.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`referrals-filter-${s}`}
            className={`text-xs font-sans uppercase tracking-wider rounded-sm px-2.5 py-1 border ${
              filter === s ? STATUS[s].color : 'border-hg-border text-hg-text-dim hover:border-hg-green'
            }`}
          >
            {STATUS[s].label} {counts[s] ? `(${counts[s]})` : ''}
          </button>
        ))}
        {counts.unassigned ? (
          <span className="text-xs font-sans text-hg-text-dim ml-1">{counts.unassigned} unassigned</span>
        ) : null}
        <label
          className="ml-auto flex items-center gap-1.5 text-xs font-sans text-hg-text-dim cursor-pointer select-none"
          data-testid="referrals-show-test"
        >
          <input
            type="checkbox"
            checked={showTest}
            onChange={(e) => setShowTest(e.target.checked)}
            className="accent-hg-green w-3.5 h-3.5"
          />
          Show test
        </label>
      </div>

      {shown.length === 0 ? (
        <div className="bg-hg-surface border border-hg-border rounded-sm p-10 text-center" data-testid="referrals-empty">
          <Gift className="mx-auto text-hg-text-dim mb-3" />
          <p className="text-hg-text-dim font-sans text-sm">
            No referrals{filter !== 'all' ? ` with status "${filter}"` : ''} yet. They'll appear here as customers
            share friends through the survey.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {shown.map((r) => {
            const st = STATUS[r.status] || STATUS.new;
            const busy = busyId === r.id;
            return (
              <div key={r.id} className={`bg-hg-surface border rounded-sm p-4 ${r.is_test ? 'border-amber-400/50' : 'border-hg-border'}`} data-testid="referral-card">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-display text-lg text-hg-text tracking-wide">{r.friend_name || '(no name)'}</span>
                      {r.is_test && (
                        <span className="text-[10px] font-sans font-bold uppercase tracking-wider rounded-sm px-1.5 py-0.5 border border-amber-400/60 text-amber-400 bg-amber-400/10" data-testid="referral-test-badge">
                          TEST
                        </span>
                      )}
                      <span className={`text-[10px] font-sans uppercase tracking-wider rounded-sm px-1.5 py-0.5 border ${st.color}`}>
                        {st.label}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-hg-text-dim font-sans">
                      {r.friend_email && (
                        <span className="inline-flex items-center gap-1"><Mail size={13} /> {r.friend_email}</span>
                      )}
                      {r.friend_city && (
                        <span className="inline-flex items-center gap-1"><MapPin size={13} /> {r.friend_city}</span>
                      )}
                      <span className="inline-flex items-center gap-1"><Clock size={13} /> {fmtDate(r.submitted_at)}</span>
                    </div>
                  </div>
                </div>

                {/* referrer attribution */}
                <div className="mt-3 pt-3 border-t border-hg-border text-sm text-hg-text-dim font-sans">
                  <span className="inline-flex items-center gap-1">
                    <User size={13} /> Referred by <span className="text-hg-text">{r.referrer_name || '—'}</span>
                    {r.referrer_email ? <span className="text-hg-text-dim">&lt;{r.referrer_email}&gt;</span> : null}
                  </span>
                  {r.referrer_fitter_name ? (
                    <span className="ml-3">· their fitter: <span className="text-hg-text">{r.referrer_fitter_name}</span></span>
                  ) : null}
                </div>

                {/* assign + status controls */}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <select
                    value={r.assigned_fitter_id || ''}
                    disabled={busy}
                    onChange={(e) => assign(r, e.target.value)}
                    data-testid="referral-assign"
                    className="bg-hg-bg border border-hg-border rounded-sm px-2 py-1.5 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green"
                  >
                    <option value="">{r.assigned_fitter_name ? `Assigned: ${r.assigned_fitter_name}` : 'Assign a fitter…'}</option>
                    {fitters.map((f) => (
                      <option key={f.id} value={f.id}>{f.name}{f.city ? ` — ${f.city}` : ''}</option>
                    ))}
                  </select>

                  <div className="flex flex-wrap gap-1">
                    {STATUS_ORDER.map((s) => (
                      <button
                        key={s}
                        disabled={busy || r.status === s}
                        onClick={() => setStatus(r, s)}
                        data-testid={`referral-status-${s}`}
                        className={`text-[11px] font-sans uppercase tracking-wider rounded-sm px-2 py-1 border transition-colors ${
                          r.status === s ? STATUS[s].color + ' cursor-default'
                            : 'border-hg-border text-hg-text-dim hover:border-hg-green hover:text-hg-green'
                        }`}
                      >
                        {STATUS[s].label}
                      </button>
                    ))}
                  </div>
                  {busy && <Loader2 size={14} className="animate-spin text-hg-green" />}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
